import { useState, useCallback } from 'react';

/**
 * Validates scheduled call form data
 * @param {string} scheduledDateTime - Scheduled date and time
 * @param {string} duration - Duration in minutes
 * @returns {Object} Validation result with isValid and error message
 */
const validateScheduleCallForm = (scheduledDateTime, duration) => {
  if (!scheduledDateTime) {
    return { isValid: false, error: 'Por favor selecciona una fecha y hora' };
  }

  const durationNum = parseInt(duration);
  if (isNaN(durationNum) || durationNum < 15 || durationNum > 480) {
    return { isValid: false, error: 'La duración debe ser entre 15 y 480 minutos' };
  }

  const selectedDate = new Date(scheduledDateTime);
  if (selectedDate <= new Date()) {
    return { isValid: false, error: 'La fecha y hora deben ser en el futuro' };
  }

  return { isValid: true, error: null };
};

/**
 * Hook for scheduling a call
 * @returns {Object} Schedule call function, loading/error states, and validation
 */
const useScheduleCall = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const scheduleCall = useCallback(async ({ conversationId, scheduledDateTime, duration, notes, meetingType = 'google_meet' }) => {
    const validation = validateScheduleCallForm(scheduledDateTime, duration);
    if (!validation.isValid) {
      setError(validation.error);
      throw new Error(validation.error);
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_STAGING_URL}/api/scheduled-calls`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          conversationId: parseInt(conversationId),
          scheduledDateTime,
          duration: parseInt(duration),
          notes: notes?.trim() || null,
          meetingType: meetingType === 'presential' ? 'presential' : 'google_meet'
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al programar la llamada');
      }

      const data = await response.json();
      return data.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    scheduleCall,
    loading,
    error,
    clearError,
    validateScheduleCallForm
  };
};

export default useScheduleCall;

